import React, { useState, useEffect, useCallback } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Divider,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

import ChartWaypointList from './ChartWaypointList';
import ChartWaypointForm from './ChartWaypointForm';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import { useApi } from '@/providers/ApiContext';
import { getChartWaypoints } from '@/actions/chartWaypoint';
import type { ChartWaypoint, EntityId } from '@/types';

interface ChartWaypointsProps {
  chartId: EntityId;
  onWaypointsChange?: () => void;
  onWaypointsCountChange?: (count: number) => void;
  isEditable?: boolean;
}

function ChartWaypoints({
  chartId,
  onWaypointsChange,
  onWaypointsCountChange,
  isEditable = true,
}: ChartWaypointsProps): React.JSX.Element {
  const api = useApi();
  const [waypoints, setWaypoints] = useState<ChartWaypoint[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [expanded, setExpanded] = useState<boolean>(true);
  const [api_error, setApiError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [waypointFormOpen, setWaypointFormOpen] = useState<boolean>(false);
  const [editingWaypoint, setEditingWaypoint] = useState<ChartWaypoint | null>(null);

  const fetchWaypoints = useCallback(async (): Promise<void> => {
    if (!chartId) return;

    try {
      setLoading(true);
      setApiError(null);
      if (!api) return;
      const response = await getChartWaypoints(api, chartId);
      const waypointsList = response.results || [];
      setWaypoints(waypointsList);
      if (onWaypointsCountChange) {
        onWaypointsCountChange(waypointsList.length);
      }
    } catch (err) {
      console.error('Error fetching waypoints:', err);
      const error = err as Error;
      setApiError(
        error.message ||
          'Unable to load chart waypoints. This could be due to network issues, server problems, or the chart may not exist.',
      );
    } finally {
      setLoading(false);
    }
  }, [api, chartId, onWaypointsCountChange]);

  useEffect(() => {
    fetchWaypoints();
  }, [fetchWaypoints]);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchWaypoints();
      if (onWaypointsChange) {
        onWaypointsChange();
      }
    }
  }, [refreshTrigger, fetchWaypoints, onWaypointsChange]);

  // Update waypoints count when waypoints change
  useEffect(() => {
    if (onWaypointsCountChange) {
      onWaypointsCountChange(waypoints.length);
    }
  }, [waypoints, onWaypointsCountChange]);

  const handleWaypointsChange = (): void => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleAddWaypoint = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    setEditingWaypoint(null);
    setWaypointFormOpen(true);
  };

  const handleEditWaypoint = (waypoint: ChartWaypoint): void => {
    setEditingWaypoint(waypoint);
    setWaypointFormOpen(true);
  };

  const handleWaypointCreated = (): void => {
    setWaypointFormOpen(false);
    setEditingWaypoint(null);
    handleWaypointsChange();
  };

  const handleCloseWaypointForm = (): void => {
    setWaypointFormOpen(false);
    setEditingWaypoint(null);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      disabled={false}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='chart-waypoints-content'
        id='chart-waypoints-header'
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', width: '100%', mr: 1 }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: 'bold',
              color: 'primary.main',
              flexGrow: 1,
            }}
          >
            Chart Waypoints
          </Typography>
          {isEditable && (
            <IconButton
              size='small'
              onClick={handleAddWaypoint}
              color='primary'
              sx={{ ml: 'auto' }}
            >
              <AddOutlinedIcon />
            </IconButton>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <Divider sx={{ mb: 2 }} />
          {loading ? (
            <Typography variant='body2' color='text.secondary'>
              Loading waypoints...
            </Typography>
          ) : (
            <ChartWaypointList
              chartId={chartId}
              waypoints={waypoints || []}
              onEdit={isEditable ? handleEditWaypoint : undefined}
              onDelete={isEditable ? handleWaypointsChange : undefined}
              isEditable={isEditable}
            />
          )}
        </Box>
      </AccordionDetails>
      <ChartWaypointForm
        open={waypointFormOpen}
        onClose={handleCloseWaypointForm}
        chartId={chartId}
        waypoint={editingWaypoint}
        onWaypointCreated={handleWaypointCreated}
      />
      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Accordion>
  );
}

export default ChartWaypoints;

